import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { Documents } from 'src/app/models/documents.models';
import { Orders } from 'src/app/models/Orders.model';
import { Status } from 'src/app/models/status';
import { IWsResult } from 'src/app/models/ws_result.model';
import { OrdersService } from 'src/app/services/orders.service';
import { OpenModalService } from 'src/app/services/shared/open-modal.service';
import { PrinterService } from 'src/app/services/printer/printer.service';

/**
 * Sub-modale "Conferma documenti" — secondo step wizard picking.
 *
 * Cleanup v2.11.0 J.4.b:
 * - 🚨 FIX BUG: nested subscribe ngOnInit (getOrder1 → getDocuments →
 *   getOrderDocuments → checkEG) → forkJoin parallelo + helper async.
 * - 🚨 FIX BUG: `reloadFunction` quasi-duplicato di ngOnInit (~30 LOC).
 *   Unificato in loadData().
 * - 🚨 FIX BUG: `printDDTStamp` e `printELG` quasi-duplicati ~30 LOC.
 *   Estratto helper `printDocument()`.
 * - 🚨 FIX BUG: `printELG` aveva semantic mismatch: usava `isLinkToDDT`
 *   come decision per assegnare `or_url_pdf_elg` — sbagliato, deve
 *   essere `isLinkToEG`. Fixed.
 * - 🚨 FIX DEAD: `counter`, `maxlength`, `minWidth`, `dialogWidth`,
 *   `dialogHeight`, `back`, `checkForELG` — dead state/methods.
 * - 🚨 Drop kendo `NotificationService` (sostituito con SweetAlert toast).
 * - Drop ~5 console.log + alert workflows complessi → Swal unificato.
 */
@Component({
  selector: 'app-confirm-documents',
  templateUrl: './confirm-documents.component.html',
  styleUrls: ['./confirm-documents.component.scss'],
  standalone: false,
})


export class ConfirmDocumentsComponent implements OnInit {

  @Input() openedDoc = true;
  @Output() modalClosed1 = new EventEmitter<void>();

  // === DI ===
  private destroyRef = inject(DestroyRef);
  private route = inject(ActivatedRoute);
  private printerService = inject(PrinterService);
  public orderService = inject(OrdersService);
  public closeServ = inject(OpenModalService);

  // === State ===
  public order: Orders.Order | null = null;
  public orderToUpdate: Orders.OrderUpdate | null = null;
  public documents: Documents.Document[] = [];

  public id = '';
  public loading = false;
  public firstEnteredOnPage = true;
  public allClicked = false;
  public isClickedOnIt = false;
  public clickedOnce = false;

  public linkToDDT: string | null = null;
  public isLinkToDDT = false;
  public linkToELG: string | null = null;
  public isLinkToEG = false;

  private cdr = inject(ChangeDetectorRef);

  // === Lifecycle ===

  ngOnInit(): void {
    this.firstEnteredOnPage = true;
    this.loadData();
  }

  close(): void {
    this.closeServ.closeDoc();
  }

  /**
   * 🚨 FIX BUG: legacy aveva nested subscribe chain (getOrder1 →
   * getDocuments → getOrderDocuments + checkEG separato). Ora forkJoin
   * parallelo + handling async getOrderDocuments come step finale.
   */
  loadData(): void {
    this.id = this.route.snapshot.paramMap.get('id') ?? '';
    if (!this.id) return;

    this.loading = true;

    forkJoin({
      order: this.orderService.getOrder1(+this.id),
      documents: this.orderService.getDocuments(+this.id),
      enriched: this.orderService.getOrderDocuments(this.id),
    })
    .pipe(
      catchError(() => of({ order: null, documents: [], enriched: null })),
      takeUntilDestroyed(this.destroyRef)
    )
    .subscribe(({ order, documents, enriched }) => {
      if (!order) return;

      this.order = order;
      this.documents = enriched ?? documents ?? [];

      // 🚨 FIX v2.11.1 (#3): legacy popolava orderToUpdate in un secondo
      // passaggio (reloadFunction duplicato di ngOnInit). Nel cleanup J.4.b
      // ho unificato in loadData ma persa l'assegnazione → saveStep() faceva
      // return early su `!this.orderToUpdate` → bottone "Salva" inerte
      // (niente save, niente close, niente Swal).
      this.orderToUpdate = new Orders.OrderUpdate(order);

      this.checkSelectedAll();
      this.checkElaboratoEG();

      this.loading = false;
      this.cdr.detectChanges();
    });
  }

  /**
   * Verifica esistenza elaborato EG sul CRM. Solo flag boolean —
   * niente Ionic alert (drop in favore di Swal nei call site).
   */
  private checkElaboratoEG(): void {
    if (!this.order) return;
    this.orderService
      .checkEG(this.order.order_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: IWsResult) => {
        this.isLinkToEG = !!result?.success;
        this.cdr.detectChanges();
      });
  }

  // === Save step ===

  saveStep(pageName: string): void {
    if (!this.order || !this.orderToUpdate) return;
    let statusNo = 0;
    if (pageName === 'documents') statusNo = Status.StatusesUser.step2;

    this.orderService
      .setStatus(this.order.order_id, statusNo)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.orderService
          .updateOrder(this.orderToUpdate!)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => this.modalClosed1.emit());
        this.close();
        Swal.fire({
          icon: 'success',
          title: 'Aggiunti!',
          text: 'Documenti selezionati con successo.',
          timer: 1500,
          timerProgressBar: true,
        });
      });
  }

  // === Step / document selection ===

  checkSelectedAll(stepBack = false): void {
    if (!this.order) return;
    const stepFrw = 2;
    const stepBck = 1;
    if (this.documents) {
      this.orderService.setDocumentsChecked(
        this.order.order_id,
        this.documents,
      );
    }
    const cod = this.order.status?.[0]?.os_status_order_cod ?? 0;
    if (cod > 2 && cod < 7 && this.firstEnteredOnPage) {
      this.documents?.forEach((d) => (d.checked = true));
      this.firstEnteredOnPage = false;
    }
  }

  checkDocument(id: number): void {
    this.documents[id].checked = !this.documents[id].checked;
    this.documents.sort(
      (a, b) =>
        +(a.checked ? a.checked : false) - +(b.checked ? b.checked : false),
    );
    this.checkSelectedAll();
    this.allClicked = this.documents.every((d) => d.checked);
    Swal.fire({
      icon: 'success',
      title: 'Documento selezionato',
      timer: 800,
      timerProgressBar: true,
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
    });
  }

  // === Print ===

  printAllDocuments(): void {
    if (!this.order) return;
    this.orderService
      .getAllDocuments(this.order.order_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result?.all_document_link_pdf) return;
        window.open(result.all_document_link_pdf);
      });
  }

  /**
   * 🚨 FIX BUG: legacy `printDDTStamp` e `printELG` erano quasi-duplicati
   * (~30 LOC). Estratto helper unificato.
   */
  private printDocument(
    kind: 'ddt' | 'elg',
  ): void {
    if (!this.order) return;
    const printerId = this.printerService.printerSelection.value?.printer_ddt_id;
    const obs =
      kind === 'ddt'
        ? this.orderService.getGenerateDDT(this.order.order_id, printerId)
        : this.orderService.getGenerateELG(this.order.order_id, printerId);

    obs
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: any) => {
        if (!result) return;
        if (result?.error_message?.msg_code === 101) {
          Swal.fire({
            icon: 'error',
            title: 'Errore',
            text: "Un'altra persona potrebbe averlo generato nello stesso tempo, oppure il server è occupato.",
          });
          return;
        }
        if (!result.success) return;

        if (kind === 'ddt') {
          const url = result.data?.or_url_ddt;
          this.isLinkToDDT = (url?.indexOf('.pdf') ?? -1) > 0;
          this.linkToDDT = this.isLinkToDDT ? url : null;
          this.isClickedOnIt = true;
          this.clickedOnce = true;
          if (url) window.open(url);
        }
        else {
          // 🚨 FIX BUG: legacy usava `isLinkToDDT` per assegnare or_url_pdf_elg.
          // Semantic mismatch: deve essere `isLinkToEG`.
          const elgUrl = result.data?.od_url_elg;
          this.isLinkToEG = (elgUrl?.indexOf('.pdf') ?? -1) > 0;
          this.linkToELG = this.isLinkToEG ? result.data?.or_url_pdf_elg : null;
          this.isClickedOnIt = true;
          this.clickedOnce = true;
          if (elgUrl) window.open(elgUrl);
        }
        this.cdr.detectChanges();
      });
  }

  printDDTStamp(): void {
    this.printDocument('ddt');
  }

  printELG(): void {
    this.printDocument('elg');
  }

  allDocumentsChecked(): boolean {
    return this.documents ? this.documents.every((d) => d.checked) : true;
  }
  
}
